"use client";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Papa from "papaparse";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { saveAs } from "file-saver";
import { useCostCodeMap } from "./useCostCodeMap";
import AlertDialog from "./AlertDialog";
import COPricingSectionsV3 from "./COPricingSectionsV3";
import COTakeoffLauncher from "./COTakeoffLauncher";
import COImportTandMModal from "./COImportTandMModal";
const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const cls = (...c) => c.filter(Boolean).join(" ");

const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${s})` : `$${s}`;
};
const fmtPDF = (n) => {
  const abs = Math.abs(n || 0);
  const s = `$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (n || 0) < 0 ? `(${s})` : s;
};

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// ── Save-time section enrichment ───────────────────────────────────────────
const _SF_LF = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
const _isCovUom = (uom) =>
  _SF_LF.some((u) => (uom || "").toLowerCase().includes(u));

function _calcAutoPrice(item, markupPct) {
  const markup = 1 + (markupPct || 0) / 100;
  const cov =
    item.category === "M" && _isCovUom(item.uom)
      ? parseFloat(item.coveragePerUnit) || 1
      : 1;
  const base = item.category === "L" ? item.unitRate || 0 : item.unitCost || 0;
  return Number((base * cov * markup).toFixed(2));
}

// Shared merge helper — paste into both COFormV3 and CODetailV3
const buildCombinedPDF = async (coBlob, tandmBlobs) => {
  if (!tandmBlobs.length) return coBlob;
  const { mergePDFBlobs } = await import("./TimeAndMaterialTab3");
  return mergePDFBlobs([coBlob, ...tandmBlobs.map((t) => t.blob)]);
};

function _computeEnrichedSections(sections, items, markupPct) {
  return sections.map((s) => {
    const bdItems = (items || []).filter(
      (i) => i.referenceNumber === s.number && i.includeInBreakdown,
    );
    const customItems = s.customItems || [];

    const hasBdM =
      bdItems.filter((i) => i.category === "M").length +
        customItems.filter((c) => c.category === "M").length >
      0;
    const hasBdL =
      bdItems.filter((i) => i.category === "L").length +
        customItems.filter((c) => c.category === "L").length >
      0;

    let matTotal = 0,
      labTotal = 0;

    bdItems.forEach((item) => {
      const qty = item.breakdownQty ?? Math.abs(item.deltaQty || 0);
      const price =
        item.breakdownUnitPrice != null
          ? item.breakdownUnitPrice
          : _calcAutoPrice(item, markupPct);
      const t = (parseFloat(qty) || 0) * (parseFloat(price) || 0);
      if (item.category === "M") matTotal += t;
      else labTotal += t;
    });

    customItems.forEach((ci) => {
      const t = (parseFloat(ci.qty) || 0) * (parseFloat(ci.unitPrice) || 0);
      if (ci.category === "M") matTotal += t;
      else labTotal += t;
    });

    return {
      ...s,
      materialPrice: hasBdM ? +matTotal.toFixed(2) : s.materialPrice || 0,
      laborPrice: hasBdL ? +labTotal.toFixed(2) : s.laborPrice || 0,
    };
  });
}

const SF_LF = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
const isCovUom = (uom) =>
  SF_LF.some((u) => (uom || "").toLowerCase().includes(u));

// ── Mapping helpers ───────────────────────────────────────────────────────
function isMapped(item) {
  if (item.category === "L") return !!item.mappedWO3;
  return !!item.mappedPO3;
}
export function countUnmapped(items = []) {
  return (items || []).filter((i) => !isMapped(i)).length;
}
function getMappingOptions(item, purchaseOrders = [], workOrders = []) {
  if (item.category === "L") {
    return {
      label: "Map to WO",
      field: "mappedWO3",
      value: item.mappedWO3 || "",
      options: (workOrders || []).map((w) => ({
        value: w._id?.toString(),
        label: `WO#${w.woNumber} — ${w.title || w.bidArea || ""} (${(w.type || "").replace(/_/g, " ")})`,
      })),
    };
  }
  return {
    label: "Map to PO",
    field: "mappedPO3",
    value: item.mappedPO3 || "",
    options: (purchaseOrders || []).map((p) => ({
      value: p._id?.toString(),
      label: `PO#${p.poNumber} — ${p.title || p.bidArea || ""}`,
    })),
  };
}
function suggestMapping(item, purchaseOrders = [], workOrders = []) {
  const bidArea = (item.bidArea || "").toLowerCase().trim();
  if (!bidArea) return { field: null, id: null };
  if (item.category === "L") {
    const wo =
      workOrders.find(
        (w) => (w.bidArea || "").toLowerCase().trim() === bidArea,
      ) ||
      workOrders.find((w) => (w.title || "").toLowerCase().includes(bidArea));
    return { field: "mappedWO3", id: wo?._id || null };
  }
  const po =
    purchaseOrders.find(
      (p) => (p.bidArea || "").toLowerCase().trim() === bidArea,
    ) ||
    purchaseOrders.find((p) => (p.title || "").toLowerCase().includes(bidArea));
  return { field: "mappedPO3", id: po?._id || null };
}

// ── Bid Area Combobox ─────────────────────────────────────────────────────
function BidAreaCombobox({
  value,
  onChange,
  knownAreas = [],
  placeholder = "Area…",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value || "");

  const ref = useRef(null);
  useEffect(() => {
    setInput(value || "");
  }, [value]);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const filtered = useMemo(() => {
    if (!input.trim()) return knownAreas;
    const q = input.toLowerCase();
    return knownAreas.filter((a) => a.toLowerCase().includes(q));
  }, [input, knownAreas]);
  const select = (area) => {
    setInput(area);
    onChange(area);
    setOpen(false);
  };
  const handleBlur = () => {
    setTimeout(() => {
      onChange(input);
      setOpen(false);
    }, 150);
  };
  return (
    <div ref={ref} className={cls("relative", className)}>
      <input
        value={input}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setInput(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onBlur={handleBlur}
        className="w-full bg-transparent border-b border-transparent hover:border-gray-500 focus:border-orange-500 focus:outline-none text-gray-300 text-[11px] py-0.5"
      />
      {open && knownAreas.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl min-w-[160px] max-h-40 overflow-y-auto">
          {filtered.length === 0 && input.trim() ? (
            <div
              className="px-3 py-2 text-xs text-orange-300 hover:bg-gray-700 cursor-pointer"
              onMouseDown={() => select(input.trim())}
            >
              + Add "{input.trim()}"
            </div>
          ) : (
            filtered.map((area) => (
              <div
                key={area}
                onMouseDown={() => select(area)}
                className={cls(
                  "px-3 py-2 text-xs cursor-pointer hover:bg-gray-700",
                  area === value
                    ? "text-orange-300 bg-gray-700/50"
                    : "text-gray-200",
                )}
              >
                {area}
              </div>
            ))
          )}
          {input.trim() &&
            filtered.length > 0 &&
            !knownAreas.includes(input.trim()) && (
              <div
                className="px-3 py-2 text-xs text-orange-300 hover:bg-gray-700 cursor-pointer border-t border-gray-700"
                onMouseDown={() => select(input.trim())}
              >
                + Add "{input.trim()}"
              </div>
            )}
        </div>
      )}
    </div>
  );
}

// ── Mapping dropdown cell ─────────────────────────────────────────────────
function MappingSelectCell({
  item,
  purchaseOrders,
  workOrders,
  isReadOnly,
  onUpdate,
  minWidth = "min-w-[180px]",
}) {
  const { label, field, value, options } = getMappingOptions(
    item,
    purchaseOrders,
    workOrders,
  );
  return (
    <td className={cls("px-2 py-1.5", minWidth)}>
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] text-gray-600 uppercase tracking-wide">
          {label}
        </span>
        <select
          value={value}
          disabled={isReadOnly}
          onChange={(e) => {
            if (!isReadOnly && onUpdate) {
              const val = e.target.value || null;
              if (field === "mappedPO3")
                onUpdate(item._localId, "mappedPO3", val, "mappedWO3");
              else onUpdate(item._localId, "mappedWO3", val, "mappedPO3");
            }
          }}
          className={cls(
            "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
            !!value ? "border-purple-600/50" : "border-gray-600",
            isReadOnly ? "opacity-80 cursor-default" : "",
          )}
        >
          <option value="">— None —</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </td>
  );
}

// ── Section Number Select Cell ────────────────────────────────────────────
function SectionSelectCell({ value, onChange, costCodes, isReadOnly }) {
  const activeCodes = (costCodes || []).filter((c) => c.active);
  const selectedCode = activeCodes.find((c) => c.code === value);
  if (isReadOnly) {
    return (
      <td className="px-2 py-1.5 min-w-[140px]">
        <span
          className={cls(
            "text-[10px] font-mono px-1.5 py-0.5 rounded",
            value
              ? "text-indigo-300 bg-indigo-900/30"
              : "text-red-500 bg-red-900/20",
          )}
        >
          {value || "missing"}
        </span>
        {selectedCode && (
          <span className="block text-[9px] text-gray-500 truncate max-w-[130px]">
            {selectedCode.name}
          </span>
        )}
      </td>
    );
  }
  return (
    <td className="px-2 py-1.5 min-w-[180px]">
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] text-gray-600 uppercase tracking-wide">
          Section #*
        </span>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={cls(
            "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
            value ? "border-orange-600/50" : "border-red-600/60",
          )}
        >
          <option value="">— Required —</option>
          {activeCodes.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>
    </td>
  );
}

// ── CSV Upload Modal ──────────────────────────────────────────────────────
export function CSVUploadModal({
  open,
  onClose,
  databaseItems,
  purchaseOrders,
  workOrders,
  sections,
  onAdd,
  toast,
  knownBidAreas,
  costCodes = [],
}) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [rowMapping, setRowMapping] = useState({});
  const [rowRef, setRowRef] = useState({});
  const [rowSection, setRowSection] = useState({});

  const [parsing, setParsing] = useState(false);
  const fRef = useRef(null);
  const activeCodes = (costCodes || []).filter((c) => c.active);

  const dbMap = useMemo(() => {
    const m = new Map();
    (databaseItems || []).forEach((i) => {
      if (i.itemCode) m.set(i.itemCode.toLowerCase().trim(), i);
    });
    return m;
  }, [databaseItems]);

  const parseFile = (f) => {
    setParsing(true);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      encoding: "latin1",
      complete: ({ data }) => {
        const valid = [],
          errs = [],
          defMapping = {},
          defSection = {};
        data.forEach((row, idx) => {
          const cat = (row["Category"] || "").trim().toUpperCase();
          if (cat !== "M" && cat !== "L") return;
          const code = (
            cat === "M"
              ? row["Mat. Code"] || row["Material Code"]
              : row["Labor Code"] || ""
          ).trim();
          if (!code) return;
          const dbItem = dbMap.get(code.toLowerCase());
          if (!dbItem) {
            errs.push({ code, row: idx + 2, reason: "Not in item database" });
            return;
          }
          const deltaQty =
            parseFloat(
              row["Delta Qty"] || row["Req Qty"] || row["Quantity"] || 0,
            ) || 0;
          const bidArea =
            (row["Area"] || row["Bid Area"] || "").trim() || "(unassigned)";

          // ── Section number — CSV row wins, then DB item fallback ──────────
          const sectionNumber =
            (row["Section"] || row["Section #"] || "").trim() ||
            (dbItem.sectionNumber || "").trim() ||
            (dbItem.costCode?.code || "").trim() ||
            (typeof dbItem.costCode === "string"
              ? dbItem.costCode.trim()
              : "") ||
            "";

          const unitCost = dbItem.defaultCost || 0;
          const absQty = Math.abs(deltaQty);
          const cov =
            cat === "M" && isCovUom(dbItem.uom)
              ? parseFloat(dbItem.coveragePerUnit) || 1
              : 1;
          const amount =
            deltaQty < 0 ? -(absQty * cov * unitCost) : absQty * cov * unitCost;
          const sug = suggestMapping(
            { category: cat, bidArea },
            purchaseOrders,
            workOrders,
          );
          if (sug.id) defMapping[idx] = { field: sug.field, id: sug.id };
          if (sectionNumber) defSection[idx] = sectionNumber;

          valid.push({
            _rowIdx: idx,
            _localId: `csv-${Date.now()}-${idx}`,
            itemDatabaseId: dbItem._id,
            itemCode: code,
            matCode: cat === "M" ? code : "",
            laborCode: cat === "L" ? code : "",
            category: cat,
            costCodeDescription: dbItem.description || "",
            itemDescription: dbItem.description || "",
            customerDescription: dbItem.description || "",
            size: dbItem.size || "",
            uom: dbItem.uom || "EA",
            sectionNumber, // ← written directly onto the item
            coveragePerUnit: parseFloat(dbItem.coveragePerUnit) || null,
            unitCost: cat === "M" ? unitCost : 0,
            unitRate: cat === "L" ? unitCost : 0,
            deltaQty,
            quantity: absQty,
            amount,
            bidArea,
            mappedPO3: null,
            mappedWO3: null,
            mappingConfirmed: false,
            referenceNumber: null,
            includeInBreakdown: false,
            breakdownQty: absQty,
            breakdownUnitPrice: 0,
            breakdownDescription: dbItem.description || "",
            breakdownUnit: dbItem.uom || "EA",
            forBudget: true,
          });
        });
        setParsed(valid);
        setErrors(errs);
        setRowMapping(defMapping);
        setRowSection(defSection);
        setSelected(new Set(valid.map((_, i) => i)));
        setParsing(false);
      },
      error: (err) => {
        toast("error", err.message);
        setParsing(false);
      },
    });
  };

  const handleAdd = () => {
    const selectedItems = parsed.filter((_, i) => selected.has(i));
    const missingSec = selectedItems.filter(
      (item) => !rowSection[item._rowIdx],
    );
    if (missingSec.length > 0) {
      toast("error", `${missingSec.length} item(s) need a section number`);
      return;
    }
    const items = selectedItems.map((item) => {
      const mp = rowMapping[item._rowIdx];
      return {
        ...item,
        sectionNumber: rowSection[item._rowIdx] || "",
        mappedPO3: mp?.field === "mappedPO3" ? mp.id : null,
        mappedWO3: mp?.field === "mappedWO3" ? mp.id : null,
        mappingConfirmed: !!mp?.id,
        referenceNumber: rowRef[item._rowIdx] ?? null,
      };
    });
    onAdd(items);
    setParsed([]);
    setErrors([]);
    setSelected(new Set());
    setRowMapping({});
    setRowRef({});
    setRowSection({});
    setFile(null);
    if (fRef.current) fRef.current.value = "";
    onClose();
  };

  const reset = () => {
    setParsed([]);
    setErrors([]);
    setSelected(new Set());
    setRowSection({});
    setFile(null);
    if (fRef.current) fRef.current.value = "";
  };

  const refOpts = [
    { value: "", label: "— None —" },
    ...(sections || []).map((s) => ({
      value: s.number,
      label: `§${s.number} — ${s.title || `Section ${s.number}`}`,
    })),
  ];

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div
        className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl flex flex-col w-full max-w-6xl mx-4"
        style={{ maxHeight: "88vh" }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-white">
              Import Items from CSV
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              M items auto-map to PO, L items auto-map to WO by bid area.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {parsed.length === 0 ? (
            <div
              className="border-2 border-dashed border-gray-600 rounded-xl p-10 text-center hover:border-orange-500 transition-colors cursor-pointer"
              onClick={() => fRef.current?.click()}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  <p className="text-gray-400 text-sm">Parsing…</p>
                </div>
              ) : file ? (
                <p className="text-green-400 font-medium">{file.name}</p>
              ) : (
                <div>
                  <p className="text-3xl text-gray-600 mb-2">↑</p>
                  <p className="text-sm text-gray-300">
                    Drop CSV or click to browse
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Columns: Category, Area, Mat. Code / Labor Code, Delta Qty,
                    Section #
                  </p>
                </div>
              )}
              <input
                ref={fRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setFile(f);
                    parseFile(f);
                  }
                  e.target.value = "";
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-green-900/20 border border-green-700/40 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-400">
                    {parsed.length}
                  </p>
                  <p className="text-xs text-gray-400">Valid</p>
                </div>
                <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-400">
                    {errors.length}
                  </p>
                  <p className="text-xs text-gray-400">Skipped</p>
                </div>
                <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-400">
                    {selected.size}
                  </p>
                  <p className="text-xs text-gray-400">Selected</p>
                </div>
              </div>
              {/* Missing section warning */}
              {(() => {
                const missCount = parsed.filter(
                  (item, i) => selected.has(i) && !rowSection[item._rowIdx],
                ).length;
                if (missCount === 0) return null;
                return (
                  <div className="bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-2.5 flex items-center gap-2">
                    <span className="text-red-400">⚠</span>
                    <span className="text-xs text-red-300">
                      {missCount} selected item{missCount !== 1 ? "s" : ""}{" "}
                      still need a section number
                    </span>
                  </div>
                );
              })()}
              <div className="rounded-xl border border-gray-700 overflow-hidden">
                <div className="flex justify-between items-center px-4 py-2.5 bg-gray-900/40 border-b border-gray-700">
                  <h4 className="text-sm font-semibold text-green-400">
                    Valid Items
                  </h4>
                  <button
                    onClick={() =>
                      setSelected(
                        selected.size === parsed.length
                          ? new Set()
                          : new Set(parsed.map((_, i) => i)),
                      )
                    }
                    className="text-xs text-gray-400 hover:text-gray-200"
                  >
                    {selected.size === parsed.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-900 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={selected.size === parsed.length}
                            onChange={() =>
                              setSelected(
                                selected.size === parsed.length
                                  ? new Set()
                                  : new Set(parsed.map((_, i) => i)),
                              )
                            }
                            className="rounded border-gray-500 bg-gray-700"
                          />
                        </th>
                        {[
                          "Cat",
                          "Code",
                          "Description",
                          "Bid Area",
                          "UOM",
                          "Δ Qty",
                          "Amount",
                          "Section #*",
                          "Map to PO/WO",
                          "Pricing §",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-2 py-2 text-left text-gray-400"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/40">
                      {parsed.map((item, i) => {
                        const mp = rowMapping[item._rowIdx];
                        const mapOpts = getMappingOptions(
                          item,
                          purchaseOrders,
                          workOrders,
                        );
                        const secVal = rowSection[item._rowIdx] || "";
                        return (
                          <tr
                            key={i}
                            className={cls(
                              "hover:bg-gray-700/30",
                              selected.has(i) && "bg-green-900/10",
                            )}
                          >
                            <td className="px-2 py-1.5">
                              <input
                                type="checkbox"
                                checked={selected.has(i)}
                                onChange={() =>
                                  setSelected((p) => {
                                    const n = new Set(p);
                                    n.has(i) ? n.delete(i) : n.add(i);
                                    return n;
                                  })
                                }
                                className="rounded border-gray-500 bg-gray-700"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <span
                                className={cls(
                                  "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                                  item.category === "M"
                                    ? "bg-blue-900/60 text-blue-300"
                                    : "bg-orange-900/60 text-orange-300",
                                )}
                              >
                                {item.category}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 font-mono text-gray-300">
                              {item.itemCode}
                            </td>
                            <td className="px-3 py-1.5 text-gray-200 max-w-[140px] truncate">
                              {item.customerDescription}
                            </td>
                            <td className="px-2 py-1.5 text-gray-400 text-center">
                              {item.uom}
                            </td>
                            <td
                              className={cls(
                                "px-2 py-1.5 font-mono font-semibold text-center",
                                (item.deltaQty || 0) < 0
                                  ? "text-red-400"
                                  : "text-white",
                              )}
                            >
                              {(item.deltaQty || 0) > 0 ? "+" : ""}
                              {item.deltaQty}
                            </td>
                            <td
                              className={cls(
                                "px-2 py-1.5 font-mono",
                                (item.amount || 0) < 0
                                  ? "text-red-400"
                                  : "text-green-300",
                              )}
                            >
                              {fmtC(item.amount || 0)}
                            </td>
                            {/* Section number selector */}
                            <td className="px-2 py-1.5 min-w-[170px]">
                              <select
                                value={secVal}
                                onChange={(e) =>
                                  setRowSection((p) => ({
                                    ...p,
                                    [item._rowIdx]: e.target.value,
                                  }))
                                }
                                className={cls(
                                  "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
                                  secVal
                                    ? "border-orange-600/50"
                                    : "border-red-600/60",
                                )}
                              >
                                <option value="">— Required —</option>
                                {activeCodes.map((c) => (
                                  <option key={c.code} value={c.code}>
                                    {c.code} — {c.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            {/* Map to PO/WO */}
                            <td className="px-2 py-1.5 min-w-[180px]">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[9px] text-gray-600">
                                  {mapOpts.label}
                                </span>
                                <select
                                  value={
                                    mp?.field === mapOpts.field
                                      ? mp.id || ""
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setRowMapping((p) => ({
                                      ...p,
                                      [item._rowIdx]: e.target.value
                                        ? {
                                            field: mapOpts.field,
                                            id: e.target.value,
                                          }
                                        : undefined,
                                    }))
                                  }
                                  className={cls(
                                    "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
                                    mp?.id
                                      ? "border-purple-600/60"
                                      : "border-gray-600",
                                  )}
                                >
                                  <option value="">— None —</option>
                                  {mapOpts.options.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>
                            {/* Pricing section ref */}
                            <td className="px-2 py-1.5 min-w-[130px]">
                              <select
                                value={rowRef[item._rowIdx] ?? ""}
                                onChange={(e) =>
                                  setRowRef((p) => ({
                                    ...p,
                                    [item._rowIdx]: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  }))
                                }
                                className="w-full text-xs bg-gray-700 border border-gray-600 rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none"
                              >
                                <option value="">— None —</option>
                                {(sections || []).map((s) => (
                                  <option key={s.number} value={s.number}>
                                    §{s.number} —{" "}
                                    {s.title || `Section ${s.number}`}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {errors.length > 0 && (
                <div className="bg-red-900/10 border border-red-700/40 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-red-900/20 border-b border-red-700/40">
                    <h4 className="text-sm font-semibold text-red-400">
                      ⚠ Skipped ({errors.length})
                    </h4>
                  </div>
                  <div className="max-h-36 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-900 sticky top-0">
                        <tr>
                          {["Row", "Code", "Reason"].map((h) => (
                            <th
                              key={h}
                              className="px-2 py-2 text-left text-gray-400"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/40">
                        {errors.map((e, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1.5 text-gray-400">
                              {e.row}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-gray-300">
                              {e.code}
                            </td>
                            <td className="px-2 py-1.5 text-red-400">
                              {e.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-700 shrink-0">
          <span className="text-xs text-gray-500">
            {selected.size > 0 && (
              <span className="text-green-400">
                {selected.size} item{selected.size !== 1 ? "s" : ""} ready
              </span>
            )}
          </span>
          <div className="flex gap-3">
            {parsed.length > 0 && (
              <button
                onClick={reset}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                ← Back
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
            {parsed.length > 0 && (
              <button
                onClick={handleAdd}
                disabled={selected.size === 0}
                className="px-5 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-40"
              >
                Add {selected.size} Item{selected.size !== 1 ? "s" : ""}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function computeCovFromSize(size, uom) {
  if (!size || !isCovUom(uom)) return null;
  const s = String(size).trim();
  const parseToFeet = (str) => {
    const t = String(str).trim();
    const parseFrac = (f) => {
      const p = f.split("/");
      return p.length === 2
        ? parseFloat(p[0]) / parseFloat(p[1])
        : parseFloat(f) || 0;
    };
    const m1 = t.match(/^([\d.]+)'\s*([\d]+)[- ]([\d]+\/[\d]+)"?$/);
    if (m1)
      return parseFloat(m1[1]) + (parseFloat(m1[2]) + parseFrac(m1[3])) / 12;
    const m2 = t.match(/^([\d.]+)'([\d.]+)"?$/);
    if (m2) return parseFloat(m2[1]) + (parseFloat(m2[2]) || 0) / 12;
    if (t.endsWith("'")) return parseFloat(t) || 0;
    if (t.endsWith('"')) return (parseFloat(t) || 0) / 12;
    return parseFloat(t) || 0;
  };
  const ul = (uom || "").toLowerCase();
  if (ul.includes("sf") || ul.includes("sq") || ul.includes("sheet")) {
    const parts = s.replace(/×/g, "x").split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const w = parseToFeet(parts[0]);
      const h = parseToFeet(parts[1]);
      if (w > 0 && h > 0) return parseFloat((w * h).toFixed(4));
    }
  } else if (ul.includes("lf") || ul.includes("lin")) {
    const len = parseToFeet(s);
    if (len > 0) return parseFloat(len.toFixed(4));
  }
  return null;
}

// ── Global Items Table ────────────────────────────────────────────────────
// Drop-in replacement for the GlobalItemsTable export in COFormV3.jsx
// Adds: description search, dropdown filters (cat/uom/size/mapping),
//       cost sort (high→low / low→high), and row reorder (↑ ↓ buttons).
//
// Props are identical to the original — no changes required at call sites.
// FIX: arrows are always visible (no group-hover on <tr> which is unreliable).

export function GlobalItemsTable({
  items,
  setItems,
  purchaseOrders,
  workOrders,
  isReadOnly,
  onOpenCSV,
  onOpenDB,
  knownBidAreas,
  onOpenTandM,
  costCodes = [],
}) {
  // ── Filter / sort state ─────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterUom, setFilterUom] = useState("all");
  const [filterSize, setFilterSize] = useState("all");
  const [filterMap, setFilterMap] = useState("all"); // all | mapped | unmapped
  const [costSort, setCostSort] = useState(null); // null | "desc" | "asc"

  // ── Derived unique options ──────────────────────────────────────────────
  const uomOptions = useMemo(() => {
    const s = new Set();
    items.forEach((i) => {
      if (i.uom) s.add(i.uom);
    });
    return Array.from(s).sort();
  }, [items]);

  const sizeOptions = useMemo(() => {
    const s = new Set();
    items.forEach((i) => {
      if (i.size && i.size.trim()) s.add(i.size.trim());
    });
    return Array.from(s).sort();
  }, [items]);

  // ── Filtering + sorting pipeline ────────────────────────────────────────
  // Parallel index array so reorder operates on the real items[] positions
  const displayIndices = useMemo(() => {
    let result = items.map((_, idx) => idx);

    if (filterCat !== "all")
      result = result.filter((i) => items[i].category === filterCat);

    if (filterUom !== "all")
      result = result.filter((i) => items[i].uom === filterUom);

    if (filterSize !== "all")
      result = result.filter(
        (i) => (items[i].size || "").trim() === filterSize,
      );

    if (filterMap !== "all") {
      result = result.filter((i) => {
        const item = items[i];
        const mapped =
          item.category === "M" ? !!item.mappedPO3 : !!item.mappedWO3;
        return filterMap === "mapped" ? mapped : !mapped;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((i) => {
        const item = items[i];
        return (
          (item.customerDescription || "").toLowerCase().includes(q) ||
          (item.costCodeDescription || "").toLowerCase().includes(q) ||
          (item.itemDescription || "").toLowerCase().includes(q) ||
          (item.itemCode || "").toLowerCase().includes(q)
        );
      });
    }

    if (costSort) {
      result = [...result].sort((a, b) => {
        const costA = Math.abs(items[a].amount || 0);
        const costB = Math.abs(items[b].amount || 0);
        return costSort === "desc" ? costB - costA : costA - costB;
      });
    }

    return result;
  }, [items, filterCat, filterUom, filterSize, filterMap, search, costSort]);

  // ── Totals (full list, unaffected by filters) ───────────────────────────
  const totalM = items
    .filter((i) => i.category === "M")
    .reduce((s, i) => s + (i.amount || 0), 0);
  const totalL = items
    .filter((i) => i.category === "L")
    .reduce((s, i) => s + (i.amount || 0), 0);
  const unmapped = countUnmapped(items);
  const missingSections = items.filter((i) => !i.sectionNumber?.trim()).length;

  // ── Item updater ────────────────────────────────────────────────────────
  const updateItem = (localId, field, value, clearField = null) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i._localId !== localId) return i;
        const u = { ...i, [field]: value };
        if (clearField) u[clearField] = null;
        if (field === "mappedPO3" || field === "mappedWO3")
          u.mappingConfirmed = !!value;
        if (field === "size" && i.category === "M" && isCovUom(i.uom)) {
          const c = computeCovFromSize(value, i.uom);
          if (c !== null) u.coveragePerUnit = c;
        }
        if (["deltaQty", "size", "coveragePerUnit"].includes(field)) {
          const dq = parseFloat(field === "deltaQty" ? value : u.deltaQty) || 0;
          if (field === "deltaQty") u.deltaQty = dq;
          u.quantity = Math.abs(dq);
          const cov =
            u.category === "M" && isCovUom(u.uom)
              ? parseFloat(u.coveragePerUnit) || 1
              : 1;
          const base = u.category === "L" ? u.unitRate || 0 : u.unitCost || 0;
          const raw = u.quantity * cov * base;
          u.amount = dq < 0 ? -raw : raw;
        }
        return u;
      }),
    );
  };

  // ── Reorder — swaps within the filtered view's original indices ─────────
  const moveItem = (fromOrigIdx, direction) => {
    const pos = displayIndices.indexOf(fromOrigIdx);
    const targetPos = pos + direction;
    if (targetPos < 0 || targetPos >= displayIndices.length) return;
    const toOrigIdx = displayIndices[targetPos];
    setItems((prev) => {
      const next = [...prev];
      [next[fromOrigIdx], next[toOrigIdx]] = [
        next[toOrigIdx],
        next[fromOrigIdx],
      ];
      return next;
    });
  };

  // ── Filter helpers ──────────────────────────────────────────────────────
  const anyFilterActive =
    search.trim() ||
    filterCat !== "all" ||
    filterUom !== "all" ||
    filterSize !== "all" ||
    filterMap !== "all" ||
    costSort !== null;

  const clearFilters = () => {
    setSearch("");
    setFilterCat("all");
    setFilterUom("all");
    setFilterSize("all");
    setFilterMap("all");
    setCostSort(null);
  };

  // ── Tiny select component ───────────────────────────────────────────────
  const FilterSelect = ({ value, onChange, options, placeholder }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cls(
        "text-xs bg-gray-900 border rounded px-2 py-1.5 focus:border-orange-500 focus:outline-none transition-colors",
        value !== "all"
          ? "border-orange-500/70 text-orange-300"
          : "border-gray-600 text-gray-400",
      )}
    >
      <option value="all">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );

  // ───────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* ── Summary pills ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 text-xs">
        {[
          { label: `${items.length} Items`, str: true },
          {
            label: "M",
            value: totalM,
            color: totalM < 0 ? "text-red-400" : "text-blue-300",
          },
          {
            label: "L",
            value: totalL,
            color: totalL < 0 ? "text-red-400" : "text-orange-300",
          },
          {
            label: "Total",
            value: totalM + totalL,
            color: totalM + totalL < 0 ? "text-red-400" : "text-green-300",
          },
        ].map(({ label, value, color, str }) => (
          <div
            key={label}
            className="bg-gray-900/50 rounded-lg px-3 py-2 border border-gray-700"
          >
            <span className="text-gray-500">{label}: </span>
            {!str && (
              <span className={cls("font-semibold tabular-nums", color)}>
                {fmtC(value)}
              </span>
            )}
          </div>
        ))}
        {unmapped > 0 && (
          <div className="bg-yellow-900/30 rounded-lg px-3 py-2 border border-yellow-700/40 text-yellow-300">
            ⚠ {unmapped} unmapped
          </div>
        )}
        {missingSections > 0 && (
          <div className="bg-red-900/30 rounded-lg px-3 py-2 border border-red-700/40 text-red-300">
            ⚠ {missingSections} missing section #
          </div>
        )}
      </div>

      {/* ── Action buttons ──────────────────────────────────────────────── */}
      {!isReadOnly && (
        <div className="flex gap-2">
          <button
            onClick={onOpenDB}
            className="px-3 py-2 text-sm text-blue-300 bg-blue-900/30 border border-blue-700/40 rounded-lg hover:bg-blue-900/50"
          >
            + From Database
          </button>
          <button
            onClick={onOpenCSV}
            className="px-3 py-2 text-sm text-orange-300 bg-orange-900/30 border border-orange-700/40 rounded-lg hover:bg-orange-900/50"
          >
            ↑ Import CSV
          </button>
          <button
            onClick={onOpenTandM}
            className="px-3 py-2 text-sm text-purple-300 bg-purple-900/30 border border-purple-700/40 rounded-lg hover:bg-purple-900/50"
          >
            ＋ Import T&M
          </button>
        </div>
      )}

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 bg-gray-900/40 border border-gray-700/60 rounded-xl px-3 py-2.5">
        {/* Description search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">
            🔍
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description or code…"
            className={cls(
              "w-full pl-7 pr-6 py-1.5 text-xs bg-gray-900 border rounded focus:border-orange-500 focus:outline-none transition-colors",
              search.trim()
                ? "border-orange-500/70 text-gray-200"
                : "border-gray-600 text-gray-400 placeholder-gray-600",
            )}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs"
            >
              ×
            </button>
          )}
        </div>

        <FilterSelect
          value={filterCat}
          onChange={setFilterCat}
          placeholder="All categories"
          options={[
            { value: "M", label: "Material (M)" },
            { value: "L", label: "Labour (L)" },
          ]}
        />

        {uomOptions.length > 0 && (
          <FilterSelect
            value={filterUom}
            onChange={setFilterUom}
            placeholder="All UOMs"
            options={uomOptions}
          />
        )}

        {sizeOptions.length > 0 && (
          <FilterSelect
            value={filterSize}
            onChange={setFilterSize}
            placeholder="All sizes"
            options={sizeOptions}
          />
        )}

        <FilterSelect
          value={filterMap}
          onChange={setFilterMap}
          placeholder="Mapped: all"
          options={[
            { value: "mapped", label: "Mapped only" },
            { value: "unmapped", label: "Unmapped only" },
          ]}
        />

        {/* Cost sort toggle */}
        <button
          onClick={() =>
            setCostSort((s) =>
              s === null ? "desc" : s === "desc" ? "asc" : null,
            )
          }
          className={cls(
            "flex items-center gap-1 text-xs border rounded px-2 py-1.5 transition-colors",
            costSort
              ? "bg-orange-900/30 border-orange-500/70 text-orange-300"
              : "bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500",
          )}
          title="Sort by total cost"
        >
          <span>Cost</span>
          <span className="text-[10px]">
            {costSort === "desc" ? "↓" : costSort === "asc" ? "↑" : "↕"}
          </span>
        </button>

        {anyFilterActive && (
          <>
            <button
              onClick={clearFilters}
              className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2 ml-auto"
            >
              Clear filters
            </button>
            <span className="text-[10px] text-gray-500 whitespace-nowrap">
              {displayIndices.length} / {items.length}
            </span>
          </>
        )}
      </div>

      {/* ── Items table ─────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-xl border border-gray-700">
        <table className="w-full text-xs min-w-[1050px]">
          <thead className="bg-gray-900 sticky top-0 z-10">
            <tr>
              {!isReadOnly && (
                <th className="px-1 py-2 w-10 text-gray-600 font-medium text-center whitespace-nowrap">
                  ↕
                </th>
              )}
              {[
                "Cat",
                "Code",
                "Section #*",
                "Description",
                "Bid Area",
                "Size",
                "Unit Cost",
                "UOM",
                "Qty Δ",
                "Total Cost",
                "Map to PO/WO",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className="px-2 py-2 text-left text-gray-400 font-medium whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/40">
            {displayIndices.map((origIdx, viewPos) => {
              const item = items[origIdx];
              const isSFLF = item.category === "M" && isCovUom(item.uom);
              const unitCostVal =
                item.category === "L" ? item.unitRate || 0 : item.unitCost || 0;
              const isFirst = viewPos === 0;
              const isLast = viewPos === displayIndices.length - 1;

              return (
                <tr
                  key={item._localId || origIdx}
                  className={cls(
                    viewPos % 2 === 0 ? "bg-gray-800/30" : "bg-gray-700/10",
                    "hover:bg-gray-600/20",
                  )}
                >
                  {/* ── Reorder buttons — always visible, not hover-dependent ── */}
                  {!isReadOnly && (
                    <td className="px-1 py-1.5 w-10">
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          disabled={isFirst}
                          onClick={() => moveItem(origIdx, -1)}
                          className={cls(
                            "w-6 h-5 flex items-center justify-center rounded text-[11px] leading-none transition-colors",
                            isFirst
                              ? "text-gray-700 cursor-not-allowed"
                              : "text-gray-500 hover:text-orange-300 hover:bg-gray-700 active:bg-gray-600",
                          )}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          disabled={isLast}
                          onClick={() => moveItem(origIdx, 1)}
                          className={cls(
                            "w-6 h-5 flex items-center justify-center rounded text-[11px] leading-none transition-colors",
                            isLast
                              ? "text-gray-700 cursor-not-allowed"
                              : "text-gray-500 hover:text-orange-300 hover:bg-gray-700 active:bg-gray-600",
                          )}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                  )}

                  {/* Cat */}
                  <td className="px-2 py-1.5">
                    <span
                      className={cls(
                        "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                        item.category === "M"
                          ? "bg-blue-900/60 text-blue-300"
                          : "bg-orange-900/60 text-orange-300",
                      )}
                    >
                      {item.category}
                    </span>
                  </td>

                  {/* Code */}
                  <td className="px-2 py-1.5 font-mono text-[10px] text-gray-400">
                    {item.itemCode || "—"}
                  </td>

                  {/* Section # */}
                  <SectionSelectCell
                    value={item.sectionNumber || ""}
                    onChange={(v) =>
                      updateItem(item._localId, "sectionNumber", v)
                    }
                    costCodes={costCodes}
                    isReadOnly={isReadOnly}
                  />

                  {/* Description */}
                  <td className="px-2 py-1.5 text-gray-200 max-w-[180px] break-words">
                    {item.customerDescription ||
                      item.costCodeDescription ||
                      "—"}
                  </td>

                  {/* Bid Area */}
                  <td className="px-2 py-1.5">
                    {isReadOnly ? (
                      <span className="text-gray-400 text-[11px]">
                        {item.bidArea || "—"}
                      </span>
                    ) : (
                      <BidAreaCombobox
                        value={item.bidArea || ""}
                        onChange={(v) =>
                          updateItem(item._localId, "bidArea", v)
                        }
                        knownAreas={knownBidAreas}
                        className="w-24"
                      />
                    )}
                  </td>

                  {/* Size */}
                  <td className="px-2 py-1.5 text-center">
                    {isSFLF ? (
                      isReadOnly ? (
                        <span className="text-gray-400 text-[11px]">
                          {item.size || "—"}
                        </span>
                      ) : (
                        <input
                          value={item.size || ""}
                          onChange={(e) =>
                            updateItem(item._localId, "size", e.target.value)
                          }
                          placeholder={
                            item.uom?.toLowerCase().includes("sf")
                              ? "4x8"
                              : "12'"
                          }
                          className="w-14 text-center bg-gray-700/60 border border-blue-700/40 rounded px-1 py-0.5 text-blue-200 text-[11px] focus:border-blue-400 focus:outline-none"
                        />
                      )
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>

                  {/* Unit Cost */}
                  <td className="px-2 py-1.5 text-right font-mono">
                    {isSFLF && item.coveragePerUnit ? (
                      <>
                        <span className="text-gray-200 font-semibold">
                          {fmtC(unitCostVal * (item.coveragePerUnit || 1))}
                        </span>
                        <span className="block text-[9px] text-gray-500">
                          {fmtC(unitCostVal)}/{(item.uom || "").toUpperCase()}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-200 font-semibold">
                        {fmtC(unitCostVal)}
                      </span>
                    )}
                  </td>

                  {/* UOM */}
                  <td className="px-2 py-1.5 text-center text-[11px]">
                    {isSFLF ? (
                      <span className="text-gray-200 font-semibold">PC</span>
                    ) : (
                      <span className="text-gray-400">{item.uom || "—"}</span>
                    )}
                  </td>

                  {/* Qty Δ */}
                  <td className="px-2 py-1.5 text-center w-20">
                    {isReadOnly ? (
                      <span
                        className={cls(
                          "font-mono font-semibold tabular-nums",
                          (item.deltaQty || 0) < 0
                            ? "text-red-400"
                            : "text-white",
                        )}
                      >
                        {(item.deltaQty || 0) > 0 ? "+" : ""}
                        {item.deltaQty}
                        {item.category === "M" && isCovUom(item.uom) && (
                          <span className="text-[9px] text-gray-500 ml-0.5">
                            pc
                          </span>
                        )}
                      </span>
                    ) : (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={item.deltaQty ?? ""}
                        onChange={(e) =>
                          updateItem(
                            item._localId,
                            "deltaQty",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className={cls(
                          "w-20 text-center bg-gray-700/60 border border-gray-600 rounded px-1 py-0.5 focus:border-orange-500 focus:outline-none font-mono",
                          "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                          (item.deltaQty || 0) < 0
                            ? "text-red-400"
                            : "text-white",
                        )}
                      />
                    )}
                  </td>

                  {/* Total Cost */}
                  <td
                    className={cls(
                      "px-2 py-1.5 text-right font-mono font-semibold",
                      (item.amount || 0) < 0
                        ? "text-red-400"
                        : "text-green-300",
                    )}
                  >
                    {fmtC(item.amount || 0)}
                  </td>

                  {/* Map to PO/WO */}
                  <MappingSelectCell
                    item={item}
                    purchaseOrders={purchaseOrders}
                    workOrders={workOrders}
                    isReadOnly={isReadOnly}
                    onUpdate={updateItem}
                  />

                  {/* Delete */}
                  <td className="px-2 py-1.5">
                    {!isReadOnly && (
                      <button
                        onClick={() =>
                          setItems((p) =>
                            p.filter((x) => x._localId !== item._localId),
                          )
                        }
                        className="text-gray-600 hover:text-red-400 text-base leading-none"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {displayIndices.length === 0 && (
              <tr>
                <td
                  colSpan={isReadOnly ? 12 : 13}
                  className="py-8 text-center text-gray-500"
                >
                  {items.length === 0
                    ? "No items yet — add from database or import CSV"
                    : "No items match the current filters"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// ── Cost resolver (add just above export function GlobalDBPicker) ──────────
function resolveDbItemCost(dbItem) {
  if (dbItem.category === "L") {
    // defaultUnitRate = billed rate; defaultCost = worker rate
    return dbItem.defaultUnitRate || dbItem.defaultCost || 0;
  }

  // Material — find the default vendor's entry in vendorPrices and return its price
  const vendorPrices = dbItem.vendorPrices || [];
  const defaultVendorId =
    dbItem.defaultVendor?._id?.toString() ||
    (typeof dbItem.defaultVendor === "string" ? dbItem.defaultVendor : null);

  if (defaultVendorId) {
    const entry = vendorPrices.find(
      (vp) =>
        (vp.vendor?._id?.toString() || vp.vendor?.toString()) ===
        defaultVendorId,
    );
    if (entry) return entry.price || entry.unitRate || 0;
  }

  // No default vendor flagged — fall back to first vendor price
  if (vendorPrices.length > 0) {
    return vendorPrices[0].price || vendorPrices[0].unitRate || 0;
  }

  return dbItem.defaultCost || 0;
}

// ── Global DB Picker ──────────────────────────────────────────────────────
export function GlobalDBPicker({
  open,
  onClose,
  databaseItems,
  purchaseOrders,
  workOrders,
  onAdd,
  knownBidAreas,
  toast,
  costCodes = [],
}) {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState([]);
  const [cfgs, setCfgs] = useState({});
  const activeCodes = (costCodes || []).filter((c) => c.active);

  const filtered = useMemo(() => {
    let list = databaseItems || [];
    if (cat !== "all") list = list.filter((i) => i.category === cat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          (i.itemCode || "").toLowerCase().includes(q) ||
          (i.description || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [databaseItems, cat, search]);

  const toggle = (item) => {
    setSelected((p) => {
      const ex = p.find((s) => s.itemCode === item.itemCode);
      if (ex) {
        setCfgs((c) => {
          const n = { ...c };
          delete n[item.itemCode];
          return n;
        });
        return p.filter((s) => s.itemCode !== item.itemCode);
      }
      const sug = suggestMapping(
        { category: item.category, bidArea: item.bidArea || "" },
        purchaseOrders,
        workOrders,
      );
      const resolvedSection =
        (item.sectionNumber || "").trim() ||
        (item.costCode?.code || "").trim() ||
        (typeof item.costCode === "string" ? item.costCode.trim() : "");
      setCfgs((c) => ({
        ...c,
        [item.itemCode]: {
          deltaQty: 1,
          bidArea: item.bidArea || "",
          sectionNumber: resolvedSection,
          mappedPO3: item.category === "M" ? sug.id || "" : "",
          mappedWO3: item.category === "L" ? sug.id || "" : "",
        },
      }));
      return [...p, item];
    });
  };

  const updateCfg = (code, field, value) =>
    setCfgs((p) => ({ ...p, [code]: { ...p[code], [field]: value } }));
  const handleAdd = () => {
    const missing = selected.filter(
      (item) => !cfgs[item.itemCode]?.sectionNumber,
    );
    if (missing.length > 0) {
      toast("error", `${missing.length} item(s) are missing a section number`);
      return;
    }
    const items = selected.map((dbItem) => {
      const cfg = cfgs[dbItem.itemCode] || {};
      const unitCost = resolveDbItemCost(dbItem);
      const deltaQty = parseFloat(cfg.deltaQty) || 1;
      const absQty = Math.abs(deltaQty);

      // Resolve coverage — stored value first, then compute from size
      let resolvedCoverage = parseFloat(dbItem.coveragePerUnit) || null;
      if (!resolvedCoverage && dbItem.size && isCovUom(dbItem.uom)) {
        const computed = computeCovFromSize(dbItem.size, dbItem.uom);
        if (computed) resolvedCoverage = computed;
      }

      const cov =
        dbItem.category === "M" && isCovUom(dbItem.uom)
          ? resolvedCoverage || 1
          : 1;
      const amount =
        deltaQty < 0 ? -(absQty * cov * unitCost) : absQty * cov * unitCost;

      const resolvedSection =
        cfg.sectionNumber ||
        (dbItem.sectionNumber || "").trim() ||
        (dbItem.costCode?.code || "").trim() ||
        (typeof dbItem.costCode === "string" ? dbItem.costCode.trim() : "");
      return {
        _localId: `db-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        itemDatabaseId: dbItem._id,
        itemCode: dbItem.itemCode,
        matCode: dbItem.category === "M" ? dbItem.itemCode : "",
        laborCode: dbItem.category === "L" ? dbItem.itemCode : "",
        category: dbItem.category,
        costCodeDescription: dbItem.description || "",
        itemDescription: dbItem.description || "",
        customerDescription: dbItem.description || "",
        size: dbItem.size || "",
        uom: dbItem.uom || "EA",
        sectionNumber: resolvedSection,
        coveragePerUnit: resolvedCoverage,
        unitCost: dbItem.category === "M" ? unitCost : 0,
        unitRate: dbItem.category === "L" ? unitCost : 0,
        deltaQty,
        quantity: absQty,
        amount,
        bidArea: cfg.bidArea || "",
        mappedPO3: dbItem.category === "M" ? cfg.mappedPO3 || null : null,
        mappedWO3: dbItem.category === "L" ? cfg.mappedWO3 || null : null,
        mappingConfirmed: !!(dbItem.category === "M"
          ? cfg.mappedPO3
          : cfg.mappedWO3),
        referenceNumber: null,
        includeInBreakdown: false,
        breakdownQty: absQty,
        breakdownUnitPrice: 0,
        breakdownDescription: dbItem.description || "",
        breakdownUnit: dbItem.uom || "EA",
        forBudget: true,
      };
    });
    onAdd(items);
    setSelected([]);
    setCfgs({});
    setSearch("");
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
      <div
        className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl flex flex-col w-full max-w-5xl mx-4"
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-white">
              Add Items from Database
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              M items map to PO · L items map to WO · Section # required
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-3 border-b border-gray-700 flex gap-3 shrink-0">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-60"
          />
          <div className="flex rounded overflow-hidden border border-gray-600">
            {["all", "M", "L"].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={cls(
                  "px-3 py-1.5 text-xs font-medium",
                  cat === c
                    ? "bg-orange-500 text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                )}
              >
                {c === "all" ? "All" : c === "M" ? "Material" : "Labour"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 w-8" />
                {[
                  "Cat",
                  "Code",
                  "Description",
                  "UOM",
                  "Unit Cost",
                  "Bid Area",
                  "Section #*",
                  "Map to PO/WO",
                ].map((h) => (
                  <th key={h} className="px-2 py-2 text-left text-gray-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {filtered.map((item) => {
                const isSel = selected.some(
                  (s) => s.itemCode === item.itemCode,
                );
                const cfg = cfgs[item.itemCode] || {};
                const mapOpts = getMappingOptions(
                  {
                    category: item.category,
                    mappedPO3: cfg.mappedPO3,
                    mappedWO3: cfg.mappedWO3,
                  },
                  purchaseOrders,
                  workOrders,
                );
                const mapVal =
                  item.category === "M"
                    ? cfg.mappedPO3 || ""
                    : cfg.mappedWO3 || "";
                const mapField =
                  item.category === "M" ? "mappedPO3" : "mappedWO3";
                const secVal = cfg.sectionNumber || "";
                const secMissing = isSel && !secVal;
                return (
                  <tr
                    key={item.itemCode}
                    className={cls(
                      "hover:bg-gray-700/40 cursor-pointer",
                      isSel && "bg-blue-900/20",
                    )}
                    onClick={() => toggle(item)}
                  >
                    <td
                      className="px-3 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(item)}
                        className="rounded border-gray-500 bg-gray-700 w-4 h-4"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={cls(
                          "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                          item.category === "M"
                            ? "bg-blue-900/60 text-blue-300"
                            : "bg-orange-900/60 text-orange-300",
                        )}
                      >
                        {item.category}
                      </span>
                    </td>
                    <td className="px-2 py-2 font-mono text-gray-300">
                      {item.itemCode}
                    </td>
                    <td className="px-2 py-2 text-gray-200 max-w-xs truncate">
                      {item.description}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.uom || "—"}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gray-300">
                      {fmtC(resolveDbItemCost(item))}
                    </td>
                    <td
                      className="px-2 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isSel ? (
                        <BidAreaCombobox
                          value={cfg.bidArea || ""}
                          onChange={(v) =>
                            updateCfg(item.itemCode, "bidArea", v)
                          }
                          knownAreas={knownBidAreas}
                          className="w-28"
                          placeholder="Area…"
                        />
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    {/* Section # */}
                    <td
                      className="px-2 py-2 min-w-[170px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isSel ? (
                        <select
                          value={secVal}
                          onChange={(e) =>
                            updateCfg(
                              item.itemCode,
                              "sectionNumber",
                              e.target.value,
                            )
                          }
                          className={cls(
                            "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
                            secMissing
                              ? "border-red-600/60"
                              : "border-orange-600/50",
                          )}
                        >
                          <option value="">— Required —</option>
                          {activeCodes.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.code} — {c.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    {/* Map */}
                    <td
                      className="px-2 py-2 min-w-[180px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isSel ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[9px] text-gray-600">
                            {mapOpts.label}
                          </span>
                          <select
                            value={mapVal}
                            onChange={(e) =>
                              updateCfg(item.itemCode, mapField, e.target.value)
                            }
                            className={cls(
                              "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
                              mapVal
                                ? "border-purple-600/60"
                                : "border-gray-600",
                            )}
                          >
                            <option value="">— None —</option>
                            {mapOpts.options.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-gray-500">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-700 shrink-0">
          <span className="text-sm text-gray-400">
            <span className="font-semibold text-white">{selected.length}</span>{" "}
            selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={selected.length === 0}
              className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
            >
              Add {selected.length > 0 ? selected.length : ""} Item
              {selected.length !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Email Modal ────────────────────────────────────────────────────────────
export function EmailModal({
  open,
  onClose,
  form,
  setForm,
  onSend,
  isSending,
  contacts = [],
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-lg border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-orange-400">
            Send Change Order
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl"
          >
            ×
          </button>
        </div>
        <div className="space-y-4">
          {contacts.length > 0 && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Quick fill from contacts
              </label>
              <select
                onChange={(e) => {
                  const c = contacts.find((x) => x._id === e.target.value);
                  if (c?.email)
                    setForm((p) => ({
                      ...p,
                      to: p.to ? `${p.to}, ${c.email}` : c.email,
                    }));
                }}
                className="w-full text-sm bg-gray-700 border border-gray-600 rounded px-3 py-2 text-gray-200 focus:border-orange-500 focus:outline-none"
              >
                <option value="">— Pick contact —</option>
                {contacts.map((c) => (
                  <option key={c._id} value={c._id}>
                    {(c.firstName || "") + " " + (c.lastName || "")}{" "}
                    {c.email ? `(${c.email})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {[
            ["To", "to", "email@example.com"],
            ["Subject", "subject", ""],
          ].map(([label, key, placeholder]) => (
            <div key={key}>
              <label className="block text-xs text-gray-400 mb-1">
                {label}
              </label>
              <input
                type="text"
                value={form[key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [key]: e.target.value }))
                }
                placeholder={placeholder}
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Body</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              rows={4}
              className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={onSend}
              disabled={isSending || !form.to}
              className={cls(
                "px-4 py-2 text-sm text-white rounded",
                isSending || !form.to
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700",
              )}
            >
              {isSending ? "Sending…" : "✉ Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PDF generation ─────────────────────────────────────────────────────────
export async function generateCOPDF({
  co,
  sections,
  items,
  companyProfile,
  projectData,
  markupPct,
  overheadPct,
  headers,
}) {
  const tplRes = await fetch("/change_order_template2.docx");
  if (!tplRes.ok)
    throw new Error("Template not found at /change_order_template2.docx");
  const tplBuf = await tplRes.arrayBuffer();
  let logoB64 = null;
  if (companyProfile?.logo) {
    try {
      const lr = await fetch(companyProfile.logo);
      if (lr.ok) {
        const lb = await lr.blob();
        logoB64 = await new Promise((r) => {
          const rd = new FileReader();
          rd.onloadend = () => r(rd.result);
          rd.readAsDataURL(lb);
        });
      }
    } catch {}
  }
  const zip = new PizZip(tplBuf);
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    modules: [
      new ImageModule({
        getImage: (v) => {
          if (typeof v !== "string") return null;
          const b64 = v.replace(/^data:image\/\w+;base64,/, "");
          return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
        },
        getSize: () => [125, 90],
      }),
    ],
  });
  let subtotal = 0;

  const pricingSectionsData = (sections || [])
    .map((section, secIdx) => {
      const secNum = section.number ?? section.sectionNumber ?? secIdx + 1;
      const secNumStr = String(secNum);

      const bdItems = (items || []).filter(
        (i) => String(i.referenceNumber) === secNumStr && i.includeInBreakdown,
      );
      let matTotal = 0,
        labTotal = 0;
      const rows = [];
      bdItems.forEach((item, rowIdx) => {
        const qty = Number(item.breakdownQty ?? Math.abs(item.deltaQty || 0));
        const cov =
          item.category === "M" && isCovUom(item.uom)
            ? parseFloat(item.coveragePerUnit) || 1
            : 1;
        const base =
          item.category === "L" ? item.unitRate || 0 : item.unitCost || 0;
        const price =
          item.breakdownUnitPrice != null
            ? item.breakdownUnitPrice
            : +(base * cov * (1 + (markupPct || 0) / 100)).toFixed(2);
        const total = qty * price;
        if (item.category === "M") matTotal += total;
        else labTotal += total;
        rows.push({
          n: `${secNum}.${rowIdx + 1}`,
          description:
            item.breakdownDescription ||
            item.customerDescription ||
            item.costCodeDescription ||
            "",
          qty: qty.toFixed(2),
          unit: item.breakdownUnit || item.uom || "EA",
          unitPrice: fmtPDF(price),
          total: fmtPDF(total),
        });
      });
      (section.customItems || []).forEach((ci, rowIdx) => {
        const total = (ci.qty || 0) * (ci.unitPrice || 0);
        if (ci.category === "M") matTotal += total;
        else labTotal += total;
        rows.push({
          n: `${secNum}.${bdItems.length + rowIdx + 1}`,
          description: ci.description || "",
          qty: (+(ci.qty || 0)).toFixed(2),
          unit: ci.unit || "EA",
          unitPrice: fmtPDF(ci.unitPrice || 0),
          total: fmtPDF(total),
        });
      });
      const hasBreakdown = rows.length > 0;
      const sMatPrice = hasBreakdown ? matTotal : section.materialPrice || 0;
      const sLabPrice = hasBreakdown ? labTotal : section.laborPrice || 0;
      const sTotal = sMatPrice + sLabPrice;

      // ── Skip internal-only sections with no customer price ──
      if (sTotal === 0) return null;

      subtotal += sTotal;
      const displayRows = hasBreakdown
        ? rows
        : [
            ...(sMatPrice !== 0
              ? [
                  {
                    n: `${secNum}.1`,
                    description: "Material",
                    qty: "",
                    unit: "",
                    unitPrice: "",
                    total: fmtPDF(sMatPrice),
                  },
                ]
              : []),
            ...(sLabPrice !== 0
              ? [
                  {
                    n: `${secNum}.2`,
                    description: "Labour",
                    qty: "",
                    unit: "",
                    unitPrice: "",
                    total: fmtPDF(sLabPrice),
                  },
                ]
              : []),
          ];
      return {
        sn: String(secNum),
        sectionTitle: section.title?.trim() || `Section ${secNum}`,
        sectionTotal: fmtPDF(sTotal),
        breakdownRows: displayRows,
      };
    })
    .filter(Boolean); // ← removes the nulls from skipped sections
  const overhead = subtotal * ((overheadPct || 0) / 100);
  const total = subtotal + overhead;
  doc.setData({
    changeOrderNumber: String(co?.changeOrderNumber || "Draft"),
    title: co?.title || "",
    projectName: projectData?.projectName || "Unknown Project",
    date: new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date()),
    attentionTo: projectData?.customerContact
      ? `${projectData.customerContact.firstName || ""} ${projectData.customerContact.lastName || ""}`.trim()
      : "N/A",
    projectAccount:
      projectData?.account?.companyName ||
      projectData?.accountId?.companyName ||
      "",
    description: co?.customerDescription || "",
    referenceNumber: co?.referenceNumber || "",
    pricingSections: pricingSectionsData,
    subtotal: fmtPDF(subtotal),
    ohPercent: (overheadPct || 0).toFixed(2),
    overhead: fmtPDF(overhead),
    total: fmtPDF(total),
    changestoSchedule: co?.changesToSchedule || 0,
    termsAndConditions:
      co?.termsAndConditions ||
      "Original terms and conditions of original contract apply.",
    companyName: companyProfile?.companyName || "",
    companyAddress: companyProfile?.address
      ? `${companyProfile.address.street || ""}, ${companyProfile.address.city || ""}, ${companyProfile.address.state || ""} ${companyProfile.address.zip || ""}`.trim()
      : "",
    image: logoB64,
  });
  doc.render();
  const docxBlob = doc.getZip().generate({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const fd = new FormData();
  fd.append(
    "docx",
    docxBlob,
    `ChangeOrder_${co?.changeOrderNumber || "Draft"}.docx`,
  );
  fd.append("documentType", "ChangeOrder");
  fd.append("identifier", String(co?.changeOrderNumber || "Draft"));
  const pdfRes = await axios.post(`${API}/upload/convert-to-pdf`, fd, {
    headers: { ...headers, "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });
  return new Blob([pdfRes.data], { type: "application/pdf" });
}

// ── CO Form ────────────────────────────────────────────────────────────────
export default function COFormV3({
  projectId,
  token,
  purchaseOrders,
  workOrders,
  databaseItems,
  companyProfile,
  projectData,
  budgetItemMap = {}, // ← add this
  coToEdit,
  onSaved,
  onCancel,
  toast,
  contractLabourRates = [],
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [saving, setSaving] = useState(false);
  const [genPDF, setGenPDF] = useState(false);
  const [unions, setUnions] = useState([]);
  const [showCSV, setShowCSV] = useState(false);
  const [showTakeoff, setShowTakeoff] = useState(false);
  const [takeoffData, setTakeoffData] = useState(null);
  const [takeoffFileUrl, setTakeoffFileUrl] = useState(null);
  const [showDB, setShowDB] = useState(false);
  const [showTandM, setShowTandM] = useState(false);
  const [dupAlert, setDupAlert] = useState({ open: false, message: "" });
  // Scope refs to flag converted after the CO is saved: [{tandmId, scopeId, ...}]
  const [convertedScopeRefs, setConvertedScopeRefs] = useState(
    coToEdit?._tandmScopeRefs || [],
  );
  const [linkedTandMScopes, setLinkedTandMScopes] = useState([]);
  const [tandmPdfBlobs, setTandmPdfBlobs] = useState([]);

  const [showEmail, setShowEmail] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contacts, setContacts] = useState([]);

  // Cost codes
  const { codeMap } = useCostCodeMap();
  const costCodes = useMemo(
    () =>
      [...codeMap.entries()].map(([code, name]) => ({
        code,
        name,
        active: true,
      })),
    [codeMap],
  );

  useEffect(() => {
    if (!token) return;
    axios
      .get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => setUnions(res.data || []))
      .catch(() => {}); // non-fatal
  }, [token]);

  const [form, setForm] = useState({
    title: coToEdit?.title || "",
    referenceNumber: coToEdit?.referenceNumber || "",
    customerDescription: coToEdit?.customerDescription || "",
    scopeOfWork: coToEdit?.scopeOfWork || "",

    // "billable" | "separate" | "nonBillable" — "billable" and "separate"
    // both build with full pricing sections; only "nonBillable" uses the
    // flat cost-items workflow.
    billingMode:
      coToEdit?.billingMode ??
      (coToEdit?.billable === false ? "nonBillable" : "billable"),
    nonBillableReason: coToEdit?.nonBillableReason || "",
    overheadPercentage: coToEdit?.overheadPercentage ?? 10,
    defaultMarkupPercentage: coToEdit?.defaultMarkupPercentage ?? 30,
    changesToSchedule: coToEdit?.changesToSchedule ?? 0,
    termsAndConditions: coToEdit?.termsAndConditions || "",
  });

  // Guard state — populated by a check against the server before allowing a
  // mode change away from what the CO currently has saved.
  const [billingModeBlockers, setBillingModeBlockers] = useState(null); // null = unchecked, [] = clear, [...] = blocked
  const [checkingBillingMode, setCheckingBillingMode] = useState(false);

  const checkBillingModeChangeable = async () => {
    if (!coToEdit?._id) return { blocked: false, blockingSovs: [] };
    setCheckingBillingMode(true);
    try {
      const res = await axios.get(
        `${API}/changeOrders3/${coToEdit._id}/billing-mode-check`,
        { headers },
      );
      const blocked = !res.data.canChangeBillingMode;
      setBillingModeBlockers(blocked ? res.data.blockingSovs : []);
      return { blocked, blockingSovs: res.data.blockingSovs || [] };
    } catch (err) {
      // Fail open on the pre-check (the PUT save still enforces the real
      // guard server-side) — just don't block the UI on a network hiccup.
      setBillingModeBlockers([]);
      return { blocked: false, blockingSovs: [] };
    } finally {
      setCheckingBillingMode(false);
    }
  };

  const handleBillingModeSelect = async (mode) => {
    if (mode === form.billingMode) return;

    const { blocked, blockingSovs } = await checkBillingModeChangeable();
    if (blocked) {
      toast(
        "error",
        `This CO has billed work on Draw ${blockingSovs
          .map((b) => `#${b.drawNumber}`)
          .join(", ")}. Remove it from that draw first (only possible if its ` +
          `billed amount there is zero) before changing billing mode.`,
      );
      return;
    }

    setForm((p) => ({
      ...p,
      billingMode: mode,
      nonBillableReason: mode === "nonBillable" ? p.nonBillableReason : "",
    }));
  };

  useEffect(() => {
    if (!coToEdit?._id || !projectId) return;
    const coId = coToEdit._id.toString();
    axios
      .get(`${API}/tandm3/project/${projectId}`, { headers })
      .then((r) => {
        const scopes = [];
        for (const tandm of r.data.tandms || []) {
          for (const scope of tandm.scopes || []) {
            const lid =
              scope.linkedChangeOrderId?._id?.toString() ||
              scope.linkedChangeOrderId?.toString?.() ||
              null;
            if (lid === coId && scope.status === "Approved") {
              scopes.push({ tandm, scope });
            }
          }
        }
        setLinkedTandMScopes(scopes);
      })
      .catch(() => {});
  }, [coToEdit?._id, projectId]);

  useEffect(() => {
    if (!coToEdit?._id) return;
    axios
      .get(`${API}/takeoffs3`, {
        headers,
        params: { projectId, changeOrderId: coToEdit._id },
      })
      .then(async (r) => {
        const data = r.data;
        if (!data?._id) return;
        setTakeoffData(data);
        if (data.gcsPath) {
          const u = await axios.get(`${API}/takeoffs3/drawing-url`, {
            headers,
            params: { path: data.gcsPath },
          });
          setTakeoffFileUrl(u.data?.url || null);
        }
      })
      .catch(() => {});
  }, [coToEdit?._id, projectId]);

  const ingestTakeoffItems = (coItems) => {
    setItems((prev) => {
      const byTakeoffId = new Map(coItems.map((i) => [i._takeoffItemId, i]));
      // drop any previous rows from the same takeoff items
      const kept = prev.filter(
        (i) => !i._takeoffItemId || !byTakeoffId.has(i._takeoffItemId),
      );
      return [...kept, ...coItems];
    });
    toast("success", `Added ${coItems.length} item(s) from takeoff`);
    // takeoff items are cost items → make sure the non-billable/items
    // tab is reachable; if billable, they still live in items[] and can
    // be assigned to a pricing section via the "unassigned" panel.
  };

  const [items, setItems] = useState(() => {
    const seen = new Set();
    return (coToEdit?.items || []).map((item, i) => {
      const resolvedSection =
        (item.sectionNumber || "").trim() ||
        (item.costCode?.code || "").trim() ||
        (typeof item.costCode === "string" ? item.costCode.trim() : "");
      // Build a candidate _localId, then guarantee uniqueness across the array
      let candidate = item._id?.toString() || item._localId || `ex-${i}`;
      while (seen.has(candidate)) {
        candidate = `${candidate}-dup-${Math.random().toString(36).slice(2, 7)}`;
      }
      seen.add(candidate);
      return {
        ...item,
        sectionNumber: resolvedSection,
        _localId: candidate,
      };
    });
  });
  const [sections, setSections] = useState(() =>
    (coToEdit?.pricingSections || []).map((s) => {
      const seen = new Set();
      return {
        ...s,
        customItems: (s.customItems || []).map((ci, idx) => {
          // Coerce whatever the backend returned into a usable string id,
          // then guarantee uniqueness within this section's customItems.
          let id =
            (ci._id && typeof ci._id === "object"
              ? ci._id.toString()
              : ci._id) ||
            `ci-load-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
          while (seen.has(id)) {
            id = `${id}-dup-${Math.random().toString(36).slice(2, 7)}`;
          }
          seen.add(id);
          return { ...ci, _id: id };
        }),
      };
    }),
  );

  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: `Change Order${coToEdit?.changeOrderNumber ? ` CO3-${coToEdit.changeOrderNumber}` : ""}${projectData?.projectName ? ` — ${projectData.projectName}` : ""}`,
    body: "Dear Client,\n\nPlease find attached the Change Order for your review.\n\nThank you,",
  });

  const knownBidAreas = useMemo(() => {
    const areas = new Set();
    (purchaseOrders || []).forEach((p) => {
      if (p.bidArea && p.bidArea !== "(unassigned)") areas.add(p.bidArea);
    });
    (workOrders || []).forEach((w) => {
      if (w.bidArea && w.bidArea !== "(unassigned)") areas.add(w.bidArea);
    });
    (items || []).forEach((i) => {
      if (i.bidArea && i.bidArea !== "(unassigned)") areas.add(i.bidArea);
    });
    return Array.from(areas).sort();
  }, [purchaseOrders, workOrders, items]);

  useEffect(() => {
    const acctId = projectData?.accountId?._id || projectData?.accountId;
    if (!acctId) return;
    axios
      .get(`${API}/contacts/related?relatedAccount=${acctId}`, { headers })
      .then((r) => setContacts(r.data || []))
      .catch(() => {});
  }, [projectData]);

  // AFTER
  const [activeTab, setActiveTab] = useState(
    form.billingMode !== "nonBillable" ? "pricing" : "items",
  );

  // AFTER
  useEffect(() => {
    setActiveTab(form.billingMode !== "nonBillable" ? "pricing" : "items");
  }, [form.billingMode]);

  const totalCost = useMemo(
    () => items.reduce((s, i) => s + (i.amount || 0), 0),
    [items],
  );
  const unmapped = useMemo(() => countUnmapped(items), [items]);
  const missingSections = useMemo(
    () => items.filter((i) => !i.sectionNumber?.trim()).length,
    [items],
  );

  const executeSave = async () => {
    setSaving(true);
    try {
      const enrichedSections = _computeEnrichedSections(
        sections,
        items,
        form.defaultMarkupPercentage,
      );
      const payload = {
        ...form,
        items: items.map(
          ({
            _localId,
            _autoMapped,
            _dbItem,
            _breakdownPriceOverridden,
            _priceFound,
            _sheetName,
            _tandmRef,
            _tandmScopeId,
            ...item
          }) => item,
        ),
        pricingSections: enrichedSections,
      };

      const res = coToEdit?._id
        ? await axios.put(`${API}/changeOrders3/${coToEdit._id}`, payload, {
            headers,
          })
        : await axios.post(
            `${API}/changeOrders3/project/${projectId}`,
            payload,
            { headers },
          );

      const saved = res.data.changeOrder;
      onSaved(saved);

      if (convertedScopeRefs.length > 0) {
        await Promise.allSettled(
          convertedScopeRefs.map(({ tandmId, scopeId }) =>
            axios.patch(
              `${API}/tandm3/${tandmId}/scopes/${scopeId}/mark-converted`,
              {
                changeOrderId: saved._id,
                changeOrderNumber: saved.changeOrderNumber,
              },
              { headers },
            ),
          ),
        );
        setConvertedScopeRefs([]);
      }
    } catch (err) {
      toast("error", err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  function findDuplicateItemsInline(items = []) {
    const SF_LF = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
    const isCov = (uom) =>
      SF_LF.some((u) => (uom || "").toLowerCase().includes(u));
    const seen = new Map();

    items.forEach((item) => {
      const sectionKey = item.referenceNumber ?? item.sectionNumber ?? "none";
      const area = (item.bidArea || "").trim().toLowerCase() || "__none__";
      const covItem = item.category === "M" && isCov(item.uom);
      const key = covItem
        ? `${sectionKey}||${area}||${item.itemCode}||${(item.size || "").trim().toLowerCase() || "__nosize__"}`
        : `${sectionKey}||${area}||${item.itemCode}`;
      if (!seen.has(key)) seen.set(key, []);
      seen.get(key).push(item);
    });

    const warnings = [];
    seen.forEach((group) => {
      if (group.length < 2) return;
      const rep = group[0];
      const desc =
        rep.customerDescription ||
        rep.costCodeDescription ||
        rep.itemCode ||
        "unknown";
      warnings.push(
        rep.category === "M" && isCov(rep.uom)
          ? `Duplicate: "${desc}" size ${rep.size || "unset"} in area "${rep.bidArea || "(no area)"}" — ${group.length}× in same section`
          : `Duplicate: "${desc}" in area "${rep.bidArea || "(no area)"}" — ${group.length}× in same section`,
      );
    });
    return warnings;
  }

  // REPLACE the save function in COFormV3
  const save = async () => {
    if (!form.title.trim()) {
      toast("error", "Title is required");
      return;
    }

    if (form.billingMode === "nonBillable") {
      const missSec = items.filter((i) => !i.sectionNumber?.trim());
      if (missSec.length > 0) {
        toast(
          "error",
          `${missSec.length} item(s) are missing a section number`,
        );
        return;
      }
    }

    const dupWarnings = findDuplicateItemsInline(items);
    if (dupWarnings.length > 0) {
      setDupAlert({
        open: true,
        message: `${dupWarnings.slice(0, 5).join("\n")}${dupWarnings.length > 5 ? `\n…and ${dupWarnings.length - 5} more` : ""}`,
      });
      return;
    }

    await executeSave();
  };

  // ── Fetch linked T&M scope blobs for this CO ──────────────────────────────
  // ── Fetch linked T&M scope blobs for this CO ──────────────────────────────

  const mergeAllPDFs = async (blobs) => {
    // blobs = array of Blob objects (already resolved, not {blob, filename})
    const { PDFDocument } = await import("pdf-lib");
    const merged = await PDFDocument.create();
    for (const blob of blobs) {
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      } catch (err) {
        console.warn("Failed to merge a PDF page:", err.message);
      }
    }
    const mergedBytes = await merged.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  };

  const fetchLinkedTandMPdfs = async () => {
    if (!coToEdit?._id) return [];
    const coId = coToEdit._id.toString();

    let allTandMs = [];
    try {
      const res = await axios.get(`${API}/tandm3/project/${projectId}`, {
        headers,
      });
      allTandMs = res.data.tandms || [];
    } catch (err) {
      console.warn("Could not fetch T&Ms:", err.message);
      return [];
    }

    const { generateScopePDF } = await import("./TimeAndMaterialTab3");
    const blobs = [];

    for (const tandm of allTandMs) {
      for (const scope of tandm.scopes || []) {
        const lid =
          scope.linkedChangeOrderId?._id?.toString() ||
          (typeof scope.linkedChangeOrderId === "string"
            ? scope.linkedChangeOrderId
            : null);
        if (lid !== coId || scope.status !== "Approved") continue;
        try {
          const result = await generateScopePDF(
            tandm,
            scope,
            projectData,
            companyProfile,
          );
          blobs.push(result.blob); // ← push .blob directly, not the whole result
        } catch (err) {
          console.warn(`Scope ${scope._id} PDF failed:`, err.message);
        }
      }
    }
    return blobs; // ← array of Blob, not array of {blob, filename}
  };

  // COFormV3
  const downloadPDF = async () => {
    if (!companyProfile) {
      toast("error", "Company profile not loaded");
      return;
    }
    setGenPDF(true);
    try {
      const coBlob = await generateCOPDF({
        co: { ...coToEdit, ...form },
        sections,
        items,
        companyProfile,
        projectData,
        markupPct: form.defaultMarkupPercentage,
        overheadPct: form.overheadPercentage,
        headers,
      });

      const tandmBlobs = await fetchLinkedTandMPdfs();
      const allBlobs = [coBlob, ...tandmBlobs];
      const finalBlob =
        allBlobs.length > 1 ? await mergeAllPDFs(allBlobs) : coBlob;

      const coNum = coToEdit?.changeOrderNumber || "Draft";
      saveAs(
        finalBlob,
        `ChangeOrder_${coNum}${tandmBlobs.length > 0 ? "_with_TandM" : ""}.pdf`,
      );

      toast(
        "success",
        tandmBlobs.length > 0
          ? `Combined PDF downloaded (CO + ${tandmBlobs.length} T&M scope${tandmBlobs.length !== 1 ? "s" : ""})`
          : "PDF downloaded",
      );
    } catch (err) {
      toast("error", `PDF error: ${err.message}`);
    } finally {
      setGenPDF(false);
    }
  };

  // COFormV3
  const prepEmail = async () => {
    if (!companyProfile) {
      toast("error", "Company profile not loaded");
      return;
    }
    setGenPDF(true);
    try {
      const coBlob = await generateCOPDF({
        co: { ...coToEdit, ...form },
        sections,
        items,
        companyProfile,
        projectData,
        markupPct: form.defaultMarkupPercentage,
        overheadPct: form.overheadPercentage,
        headers,
      });

      const tandmBlobs = await fetchLinkedTandMPdfs();
      const allBlobs = [coBlob, ...tandmBlobs];
      const finalBlob =
        allBlobs.length > 1 ? await mergeAllPDFs(allBlobs) : coBlob;

      setPdfBlob(finalBlob);
      setTandmPdfBlobs(tandmBlobs); // kept only for count display
      setShowEmail(true);
    } catch (err) {
      toast("error", `PDF error: ${err.message}`);
    } finally {
      setGenPDF(false);
    }
  };

  const sendEmail = async () => {
    if (!emailForm.to || !pdfBlob) {
      toast("error", "Fill recipient and generate PDF first");
      return;
    }
    setSendingEmail(true);
    try {
      const fd = new FormData();
      fd.append("to", emailForm.to);
      fd.append("subject", emailForm.subject);
      fd.append("body", emailForm.body || "");

      const coNum = coToEdit?.changeOrderNumber || "Draft";
      const filename =
        tandmPdfBlobs.length > 0
          ? `ChangeOrder_${coNum}_with_TandM.pdf`
          : `ChangeOrder_${coNum}.pdf`;

      fd.append("files", pdfBlob, filename);

      await axios.post(`${API}/changeOrders/${coToEdit?._id}/send-email`, fd, {
        headers,
      });

      setShowEmail(false);
      setPdfBlob(null);
      setTandmPdfBlobs([]);
      toast("success", "Email sent");
    } catch (err) {
      toast("error", err.response?.data?.message || err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const takeoffCatalog = async () => databaseItems;

  const tabs =
    form.billingMode !== "nonBillable"
      ? [
          { id: "pricing", label: `Pricing Sections (${sections.length})` },
          { id: "info", label: "Details" },
        ]
      : [
          {
            id: "items",
            label: `Cost Items (${items.length})${unmapped > 0 ? ` ⚠${unmapped}` : ""}${missingSections > 0 ? ` ⛔${missingSections}` : ""}`,
          },
          { id: "info", label: "Details" },
        ];

  return (
    <div className="space-y-5">
      <CSVUploadModal
        open={showCSV}
        onClose={() => setShowCSV(false)}
        databaseItems={databaseItems}
        purchaseOrders={purchaseOrders}
        workOrders={workOrders}
        sections={sections}
        onAdd={(newItems) => {
          setItems((p) => [...p, ...newItems]);
          toast(
            "success",
            `Added ${newItems.length} item${newItems.length !== 1 ? "s" : ""}`,
          );
        }}
        toast={toast}
        knownBidAreas={knownBidAreas}
        costCodes={costCodes}
      />
      <GlobalDBPicker
        open={showDB}
        onClose={() => setShowDB(false)}
        databaseItems={databaseItems}
        purchaseOrders={purchaseOrders}
        toast={toast}
        workOrders={workOrders}
        costCodes={costCodes}
        onAdd={(newItems) => {
          setItems((p) => [...p, ...newItems]);
          toast(
            "success",
            `Added ${newItems.length} item${newItems.length !== 1 ? "s" : ""}`,
          );
        }}
        knownBidAreas={knownBidAreas}
      />

      <EmailModal
        open={showEmail}
        onClose={() => {
          setShowEmail(false);
          setPdfBlob(null);
        }}
        form={emailForm}
        setForm={setEmailForm}
        onSend={sendEmail}
        isSending={sendingEmail}
        contacts={contacts}
      />

      <AlertDialog
        open={dupAlert.open}
        type="confirm"
        title="Duplicate Items Detected"
        message={dupAlert.message}
        confirmText="Save Anyway"
        cancelText="Go Back"
        onClose={() => setDupAlert({ open: false, message: "" })}
        onConfirm={async () => {
          setDupAlert({ open: false, message: "" });
          await executeSave();
        }}
      />

      <COTakeoffLauncher
        open={showTakeoff}
        onClose={() => setShowTakeoff(false)}
        projectId={projectId}
        token={token}
        initialData={takeoffData}
        fileUrl={takeoffFileUrl}
        getCatalog={takeoffCatalog}
        knownBidAreas={knownBidAreas}
        onIngestItems={ingestTakeoffItems}
        onTakeoffSaved={(doc) => {
          setTakeoffData(doc);
          // Persist the link on the CO so drawing + items stay synced.
          if (doc?._id && coToEdit?._id) {
            axios
              .patch(
                `${API}/changeOrders3/${coToEdit._id}/link-takeoff`,
                { takeoffId: doc._id, gcsPath: doc.gcsPath },
                { headers },
              )
              .catch(() => {});
          }
        }}
      />

      {/* Header */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-[11px] text-gray-400 mb-1">
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">
              Client Ref #
            </label>
            <input
              value={form.referenceNumber}
              onChange={(e) =>
                setForm((p) => ({ ...p, referenceNumber: e.target.value }))
              }
              placeholder="PCO-001"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {[
              ["overheadPercentage", "Overhead %"],
              ["defaultMarkupPercentage", "Markup %"],
            ].map(([key, label]) => (
              <div key={key} className="flex-1">
                <label className="block text-[11px] text-gray-400 mb-1">
                  {label}
                </label>
                <input
                  type="number"
                  value={form[key]}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      [key]: parseFloat(e.target.value) || 0,
                    }))
                  }
                  step="0.01"
                  className="w-full px-2 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Billing:</span>
            <div className="flex rounded-lg overflow-hidden border border-gray-600">
              {[
                { v: "billable", l: "Billable" },
                { v: "separate", l: "Bill Separately" },
                { v: "nonBillable", l: "Non-Billable" },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  type="button"
                  disabled={checkingBillingMode}
                  onClick={() => handleBillingModeSelect(v)}
                  className={cls(
                    "px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
                    form.billingMode === v
                      ? v === "billable"
                        ? "bg-orange-500 text-white"
                        : v === "separate"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-500 text-white"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200",
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
            {checkingBillingMode && (
              <span className="text-[10px] text-gray-500">Checking…</span>
            )}
          </div>

          {form.billingMode === "nonBillable" && (
            <select
              value={form.nonBillableReason}
              onChange={(e) =>
                setForm((p) => ({ ...p, nonBillableReason: e.target.value }))
              }
              className="px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            >
              <option value="">— Reason —</option>
              {[
                "Site Condition Change",
                "Foreman Field Decision",
                "Estimation Error",
                "Design / Engineering Change",
              ].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          {form.billingMode === "separate" && (
            <span className="text-[11px] text-purple-300 bg-purple-900/30 border border-purple-700/40 rounded px-2 py-1">
              This CO will get its own standalone invoice, separate from the
              project's normal progress draws — including its own optional 10%
              holdback.
            </span>
          )}
        </div>

        {billingModeBlockers && billingModeBlockers.length > 0 && (
          <div className="mt-2 px-3 py-2 text-xs text-red-300 bg-red-900/20 border border-red-700/40 rounded-lg">
            ⚠ This CO has billed work on{" "}
            {billingModeBlockers.map((b) => `Draw #${b.drawNumber}`).join(", ")}
            . Remove it from that draw first (only possible if its billed amount
            there is zero) before changing billing mode.
          </div>
        )}
      </div>

      {linkedTandMScopes.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-purple-700/40 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 bg-purple-900/20 border-b border-purple-700/30">
            <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">
              📋 Linked T&M Scopes
            </span>
            <span className="text-[10px] px-2 py-0.5 bg-purple-900/50 text-purple-300 border border-purple-700/50 rounded-full font-bold">
              {linkedTandMScopes.length} scope
              {linkedTandMScopes.length !== 1 ? "s" : ""}
            </span>
            <span className="text-[10px] text-purple-500">
              — PDFs included when downloading or emailing
            </span>
          </div>
          <div className="divide-y divide-gray-700/40">
            {linkedTandMScopes.map(({ tandm, scope }) => {
              const mCount = (scope.items || []).filter(
                (i) => i.category === "M",
              ).length;
              const lCount = (scope.items || []).filter(
                (i) => i.category === "L",
              ).length;
              return (
                <div
                  key={scope._id}
                  className="flex items-center gap-3 px-5 py-2.5"
                >
                  <span className="text-[10px] font-mono font-bold text-orange-400 shrink-0">
                    T&M #{tandm.timeAndMaterialNumber}
                  </span>
                  {scope.scopeNumber != null && (
                    <span className="text-[10px] font-mono text-gray-500 shrink-0">
                      Entry #{String(scope.scopeNumber).padStart(2, "0")}
                    </span>
                  )}
                  <span className="text-xs text-gray-300 font-medium truncate flex-1">
                    {scope.name}
                  </span>
                  {scope.note && (
                    <span className="text-[10px] text-gray-500 truncate max-w-[140px] shrink-0">
                      {scope.note}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {mCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/50 text-blue-300 rounded">
                        {mCount}M
                      </span>
                    )}
                    {lCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">
                        {lCount}L
                      </span>
                    )}
                    {scope.signedBy && (
                      <span className="text-[10px] text-green-400">
                        ✓ {scope.signedBy}
                      </span>
                    )}
                    {scope.approvedAt && (
                      <span className="text-[10px] text-gray-500">
                        {new Date(scope.approvedAt).toLocaleDateString()}
                      </span>
                    )}
                    <span
                      className={cls(
                        "text-[10px] px-1.5 py-0.5 rounded border",
                        scope.convertedToChangeOrder
                          ? "bg-gray-700 text-gray-500 border-gray-600"
                          : "bg-purple-900/40 text-purple-400 border-purple-700/40",
                      )}
                    >
                      {scope.convertedToChangeOrder ? "converted" : "linked"}
                    </span>
                    <button
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `Unlink "${scope.name}" from this CO? It will no longer be included in PDF exports and can be linked to a different CO.`,
                          )
                        )
                          return;
                        try {
                          await axios.patch(
                            `${API}/tandm3/${tandm._id}/scopes/${scope._id}/unlink-co`,
                            {},
                            { headers },
                          );
                          setLinkedTandMScopes((p) =>
                            p.filter((s) => s.scope._id !== scope._id),
                          );
                          setConvertedScopeRefs((p) =>
                            p.filter((r) => r.scopeId !== scope._id),
                          );
                        } catch (err) {
                          toast(
                            "error",
                            err.response?.data?.message ||
                              "Failed to unlink scope",
                          );
                        }
                      }}
                      className="text-[10px] px-2 py-0.5 rounded border border-red-800/40 text-red-400 bg-red-900/20 hover:bg-red-900/40 hover:border-red-700 transition-colors"
                      title="Unlink this scope from the CO"
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-900/50 rounded-xl p-1 w-fit">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cls(
              "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
              activeTab === id
                ? "bg-gray-700 text-orange-400 shadow"
                : "text-gray-400 hover:text-gray-200",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "pricing" && form.billingMode !== "nonBillable" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          {sections.length === 0 && (
            <div className="bg-orange-900/20 border border-orange-700/40 rounded-lg px-4 py-3 mb-4 text-xs">
              <p className="text-orange-300 font-semibold mb-0.5">
                Start by adding a pricing section
              </p>
              <p className="text-orange-400/70">
                Each section = one customer-facing category. Inside each
                section, add items from the database —{" "}
                <span className="text-white font-medium">
                  M items map to PO, L items map to WO
                </span>
                .
              </p>
            </div>
          )}

          {(() => {
            const unassigned = items.filter((i) => i.referenceNumber == null);
            if (!unassigned.length) return null;
            return (
              <div className="bg-yellow-900/20 border border-yellow-700/40 rounded-lg px-4 py-3 mb-4">
                <p className="text-yellow-300 text-xs font-semibold mb-2">
                  ⚠ {unassigned.length} item{unassigned.length !== 1 ? "s" : ""}{" "}
                  not assigned to a pricing section
                </p>
                <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                  {unassigned.map((item) => (
                    <div
                      key={item._localId}
                      className="flex items-center justify-between gap-3 text-xs text-gray-300"
                    >
                      <span className="truncate flex-1">
                        <span
                          className={cls(
                            "inline-block px-1 py-0.5 text-[10px] font-bold rounded mr-2",
                            item.category === "M"
                              ? "bg-blue-900/60 text-blue-300"
                              : "bg-orange-900/60 text-orange-300",
                          )}
                        >
                          {item.category}
                        </span>
                        {item.customerDescription || item.costCodeDescription}
                        {item.bidArea && (
                          <span className="text-gray-500 ml-1">
                            — {item.bidArea}
                          </span>
                        )}
                      </span>
                      {sections.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            const num = parseInt(e.target.value);
                            if (!num) return;
                            setItems((p) =>
                              p.map((i) =>
                                i._localId === item._localId
                                  ? { ...i, referenceNumber: num }
                                  : i,
                              ),
                            );
                          }}
                          className="text-xs bg-gray-700 border border-yellow-600/40 rounded px-2 py-1 text-gray-200 focus:border-orange-500 focus:outline-none shrink-0"
                        >
                          <option value="">— Assign to section —</option>
                          {sections.map((s) => (
                            <option key={s.number} value={s.number}>
                              §{s.number} — {s.title || `Section ${s.number}`}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
                {sections.length > 0 && (
                  <button
                    onClick={() => {
                      if (sections.length !== 1) return;
                      setItems((p) =>
                        p.map((i) =>
                          i.referenceNumber == null
                            ? { ...i, referenceNumber: sections[0].number }
                            : i,
                        ),
                      );
                    }}
                    className={cls(
                      "px-3 py-1.5 text-xs font-medium rounded border transition-colors",
                      sections.length === 1
                        ? "text-yellow-300 bg-yellow-900/30 border-yellow-700/40 hover:bg-yellow-900/50"
                        : "text-gray-500 bg-gray-700/30 border-gray-600 cursor-not-allowed",
                    )}
                    disabled={sections.length !== 1}
                    title={
                      sections.length !== 1
                        ? "Use the dropdowns above to assign each item when there are multiple sections"
                        : ""
                    }
                  >
                    {sections.length === 1
                      ? `Assign all to §${sections[0].number}`
                      : "Use dropdowns above to assign each item"}
                  </button>
                )}
              </div>
            );
          })()}

          <COPricingSectionsV3
            sections={sections}
            setSections={setSections}
            items={items}
            setItems={setItems}
            markupPct={form.defaultMarkupPercentage}
            overheadPct={form.overheadPercentage}
            isReadOnly={false}
            databaseItems={databaseItems}
            purchaseOrders={purchaseOrders}
            workOrders={workOrders}
            knownBidAreas={knownBidAreas}
            unions={unions}
            costCodes={costCodes}
            contractLabourRates={contractLabourRates}
            budgetItemMap={budgetItemMap}
            projectId={projectId} // ← add
            token={token} // ← add
            onScopeRefsImported={(
              refs, // ← add
            ) => setConvertedScopeRefs((p) => [...p, ...refs])}
          />
        </div>
      )}

      {activeTab === "items" && form.billingMode === "nonBillable" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <GlobalItemsTable
            items={items}
            setItems={setItems}
            purchaseOrders={purchaseOrders}
            workOrders={workOrders}
            isReadOnly={false}
            onOpenCSV={() => setShowCSV(true)}
            onOpenDB={() => setShowDB(true)}
            onOpenTandM={() => setShowTandM(true)} // ← add this
            knownBidAreas={knownBidAreas}
            costCodes={costCodes}
          />
        </div>
      )}

      {activeTab === "info" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["customerDescription", "Customer Description"]].map(
              ([key, label]) => (
                <div key={key}>
                  <label className="block text-[11px] text-gray-400 mb-1">
                    {label}
                  </label>
                  <textarea
                    value={form[key]}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, [key]: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>
              ),
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">
                Schedule Change (Days)
              </label>
              <input
                type="number"
                value={form.changesToSchedule}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    changesToSchedule: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={form.termsAndConditions}
                onChange={(e) =>
                  setForm((p) => ({ ...p, termsAndConditions: e.target.value }))
                }
                rows={2}
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-700 flex-wrap gap-3">
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          <span>
            Budget cost:{" "}
            <span
              className={cls(
                "font-semibold",
                totalCost < 0 ? "text-red-400" : "text-green-300",
              )}
            >
              {fmtC(totalCost)}
            </span>
          </span>
          {unmapped > 0 && (
            <span className="text-yellow-400">
              ⚠ {unmapped} unmapped (M→PO, L→WO)
            </span>
          )}
          {missingSections > 0 && (
            <span className="text-red-400">
              ⛔ {missingSections} missing section #
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          {coToEdit?._id && (
            <>
              <button
                onClick={downloadPDF}
                disabled={genPDF}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {genPDF ? "Generating…" : "↓ PDF"}
              </button>
              <button
                onClick={prepEmail}
                disabled={genPDF}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                ✉ Email
              </button>
            </>
          )}
          <button
            onClick={save}
            disabled={saving}
            className={cls(
              "px-5 py-2 text-sm font-semibold text-white rounded-lg",
              saving
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600",
            )}
          >
            {saving ? "Saving…" : coToEdit ? "Save Changes" : "Create CO"}
          </button>
          <button
            type="button"
            onClick={() => setShowTakeoff(true)}
            className="px-3 py-2 text-sm text-emerald-300 bg-emerald-900/30 border border-emerald-700/40 rounded-lg hover:bg-emerald-900/50"
          >
            ✎ Draw / Takeoff
          </button>
        </div>
      </div>
    </div>
  );
}
